import { PaletteMode } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import type { ThemeOptions } from "@mui/material/styles";
// DQ-8529 fix: augments MUI's Components type to include MuiDataGrid,
// resolving TS2322 when MuiDataGrid overrides are used in newThemeSettings.
import "@mui/x-data-grid-premium/themeAugmentation";

// ─── DQ-8527: TypeScript module augmentation ──────────────────────────────────
// Extends MUI Palette / PaletteOptions with the `neutral` key used by both
// themeSettings and newThemeSettings. Without this declaration TypeScript
// rejects `neutral` as an unknown palette key.
declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions["primary"];
  }
}

// ─── Theme 2.0 brand tokens (single source of truth, Epic DQ-8617) ────────────
export const brand = {
  navy: "#0E1223", // primary brand color
  blue: "#689ec8",
  ice: "#d7f1ff",
  teal: "#70b5a7",
  violet: "#8686db",
  black: "#000000",
  offWhite: "#fdfeff",
  white: "#ffffff",
};

// Brand gradient tokens from the brand brief
export const brandGradients = {
  navyToBlack: `linear-gradient(180deg, ${brand.navy} 0%, ${brand.black} 100%)`,
  tealToNavy: `linear-gradient(180deg, ${brand.teal} 0%, ${brand.navy} 100%)`,
  blueToNavy: `linear-gradient(180deg, ${brand.blue} 0%, ${brand.navy} 100%)`,
  iceToWhite: `linear-gradient(180deg, ${brand.ice} 0%, ${brand.offWhite} 100%)`,
};

// Tint/shade scales derived from the brand colors (white/black mixes)
export const navyScale = {
  100: "#d3d3d7",
  200: "#a7a8af",
  300: "#7b7d88",
  400: "#4f5260",
  500: brand.navy,
  600: "#1c1f2d",
  700: "#151722",
  800: "#0e0f16",
  900: "#07070b",
};

export const blueScale = {
  50: "#e6f6ff",
  100: "#e0ebf4",
  200: "#c2d8e9",
  300: "#a4c4de",
  400: "#86b1d3",
  500: brand.blue,
  600: "#537ea0",
  700: "#3e5e78",
  800: "#293f50",
  900: "#141f28",
};

// ─── Legacy dashboard color tokens (DQ-8529 — retained for newThemeSettings) ──
export const tokensDark = {
  grey: {
    0: "#ffffff", // manually adjusted
    10: "#f6f6f6", // manually adjusted
    50: "#f0f0f0", // manually adjusted
    100: "#e0e0e0",
    200: "#c2c2c2",
    300: "#a3a3a3",
    400: "#858585",
    500: "#666666",
    600: "#525252",
    700: "#3d3d3d",
    800: "#292929",
    900: "#141414",
    1000: "#000000", // manually adjusted
  },
  primary: {
    // blue
    100: "#d3d4de",
    200: "#a6a9be",
    300: "#7a7f9d",
    400: "#4d547d",
    500: "#21295c",
    600: "#191F45", // manually adjusted
    700: "#141937",
    800: "#0d1025",
    900: "#070812",
  },
  secondary: {
    // yellow
    50: "#f0f0f0", // manually adjusted
    100: "#fff6e0",
    200: "#ffedc2",
    300: "#ffe3a3",
    400: "#ffda85",
    500: "#ffd166",
    600: "#146FD0",
    700: "#997d3d",
    800: "#665429",
    900: "#332a14",
  },
};

// function that reverses the color palette
function reverseTokens(tokensDark: any) {
  const reversedTokens: any = {};
  Object.entries(tokensDark).forEach(([key, val]: any) => {
    const keys = Object.keys(val);
    const values = Object.values(val);
    const length = keys.length;
    const reversedObj: any = {};
    for (let i = 0; i < length; i++) {
      reversedObj[keys[i]] = values[length - i - 1];
    }
    reversedTokens[key] = reversedObj;
  });
  return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);

// ─── Theme 2.0 MUI theme settings (brand tokens + Nexa typography) ─────────────
export const themeSettings = (mode: PaletteMode) => {
  return {
    palette: {
      mode: mode,
      common: {
        black: brand.black,
        white: brand.white,
      },
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              ...navyScale,
              main: brand.blue, // legible against the navy dark background
              light: brand.ice,
              dark: brand.navy,
              contrastText: brand.navy,
            },
            secondary: {
              ...blueScale,
              main: brand.blue,
              light: brand.ice,
              dark: brand.navy,
              contrastText: brand.navy,
            },
            neutral: {
              main: navyScale[300],
            },
            info: {
              main: brand.blue,
              light: brand.ice,
              dark: brand.navy,
              contrastText: brand.navy,
            },
            success: {
              main: brand.teal,
              contrastText: brand.navy,
            },
            background: {
              default: brand.navy,
              alt: navyScale[600],
              paper: navyScale[600],
            },
            text: {
              primary: brand.white,
              secondary: brand.ice,
            },
            divider: "rgba(215, 241, 255, 0.2)",
          }
        : {
            // palette values for light mode
            primary: {
              ...navyScale,
              main: brand.navy,
              light: brand.blue,
              dark: brand.black,
              contrastText: brand.white,
            },
            secondary: {
              ...blueScale,
              main: brand.blue,
              light: brand.ice,
              dark: brand.navy,
              contrastText: brand.white,
            },
            neutral: {
              main: navyScale[400],
            },
            info: {
              main: brand.blue,
              light: brand.ice,
              dark: brand.navy,
              contrastText: brand.white,
            },
            success: {
              main: brand.teal,
              contrastText: brand.white,
            },
            background: {
              default: brand.white,
              alt: brand.offWhite,
              paper: brand.white,
            },
            text: {
              primary: brand.navy,
              secondary: navyScale[400],
            },
            divider: "rgba(35, 39, 57, 0.12)",
          }),
    },
    typography: {
      fontFamily: ["nexa", "sans-serif"].join(","),
      fontSize: 12,
      h1: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 24 },
      h2: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 20 },
      h3: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 18 },
      h4: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 16 },
      h5: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 14 },
      h6: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 12 },
    },
  };
};

// ─── DQ-8527: SCSS design token constants ─────────────────────────────────────
// Canonical JS mirrors of the CSS custom properties in _variables.scss.
// DQ-8674: All values updated to reflect Theme 2.0 re-pointing (Epic DQ-8617).
// Update both _variables.scss and this object when a value changes.
export const scssTokens = {
  white: "#ffffff", // --white
  black: "#0E1223", // --black              was #1e1e1e → brand.navy
  grey: "#4f5260", // --grey               was #555b69 → navyScale[400]
  lightGrey: "#a7a8af", // --light-grey         was #979797 → navyScale[200]
  darkGrey: "#0E1223", // --dark-grey          was #142a41 → brand.navy
  orange: "#689ec8", // --orange             was #f48400 → brand.blue (re-pointed as CTA)
  orange20: "rgba(104,158,200,0.20)", // --orange-20          recomputed from brand.blue
  skyBlue: "#689ec8", // --sky-blue           was #1985e5 → brand.blue
  darkBlue: "#537ea0", // --dark-blue          was #196bb4 → blueScale[600]
  darkBlue10: "rgba(83,126,160,0.10)", // --dark-blue10        recomputed from #537ea0
  darkBlue50: "rgba(83,126,160,0.50)", // --dark-blue50        recomputed from #537ea0
  badgeBlueBorder: "#537ea0", // --badge-blue-border  was #0967b1 → blueScale[600]
  inputBoxBorder: "#d3d3d7", // --input-box-border   was #cfcfcf → navyScale[100]
  cardBg: "#e6f6ff", // --card-bg            was #f8f8f8 → blueScale[50]
  chipBg: "rgba(104,158,200,0.12)", // --chip-bg            recomputed from brand.blue 12%
  successBg1: "rgb(116,184,86)", // --success-bg-1      unchanged
  badgeDangerBg: "#e84336", // --badge-danger-bg    unchanged
  tableHeaderBg: "#e6f6ff", // --table-header-bg    was #ededed → blueScale[50]
  tableRowAltBg: "rgba(83,126,160,0.05)", // 5% of --badge-blue-border (#537ea0)
  inputFocusShadow: "3px 3px 3px 0px rgba(0,0,0,0.2)", // --input-focus-shadow unchanged
  // Premium-surface presets — Comparison Dashboard.
  // Tuned for a light page background (blueScale[50] = #e6f6ff).
  surfaceGlass: "#ffffff",
  surfaceGlassHover: "#ffffff",
  elevationSoft:
    "0 1px 2px rgba(35,39,57,0.05), 0 4px 10px rgba(35,39,57,0.06), 0 12px 24px rgba(35,39,57,0.04)",
  elevationHover:
    "0 4px 10px rgba(35,39,57,0.10), 0 16px 32px rgba(83,126,160,0.18), 0 32px 64px rgba(35,39,57,0.12)",
  // Inset variant — for nested cards (e.g. Research nested under Issue Details).
  surfaceInset: "#fafbfd",
  elevationInset: "0 0 0 1px rgba(83,126,160,0.08)",
} as const;

// ─── Dark-mode surface tokens ─────────────────────────────────────────────────
// JS mirrors of the _variables.scss `html[data-theme="dark"]` overrides.
// Use these inside the factory functions below and in any mode-aware component.
export const darkModeTokens = {
  surface: navyScale[600], // #1c1f2d  (was --white light)
  surfaceInset: navyScale[700], // #151722  (was --card-bg light)
  border: "rgba(215,241,255,0.12)", // brand.ice translucent border
  borderSubtle: "rgba(215,241,255,0.06)", // hairline divider
  headerBg: "rgba(28,31,45,0.80)", // frosted-glass column header on dark
  footerBg: "rgba(28,31,45,0.70)", // frosted-glass footer on dark
  rowHover: "rgba(104,158,200,0.08)", // brand.blue 8% — row hover tint
  rowSelected: "rgba(104,158,200,0.14)", // row selected tint
  rowSelectedHover: "rgba(104,158,200,0.18)", // row selected + hover tint
  shadowSoft:
    "0 1px 4px rgba(0,0,0,0.32), 0 4px 10px rgba(0,0,0,0.24), 0 12px 24px rgba(0,0,0,0.18)",
  shadowHover:
    "0 4px 10px rgba(0,0,0,0.42), 0 16px 32px rgba(104,158,200,0.22), 0 32px 64px rgba(0,0,0,0.28)",
  elevationInset: "0 0 0 1px rgba(215,241,255,0.10)",
} as const;

// ─── Premium sx factory functions (mode-aware) ────────────────────────────────
// Call these inside components after `const { mode } = useThemeMode()`.
// The static exports below are light-mode-only backward-compat aliases for
// consumers that have not yet been updated to call the factories.

/**
 * getPremiumCardSx — DQ-8674 dark mode
 * Mode-aware version of the section card used on the Comparison Dashboard.
 */
export const getPremiumCardSx = (mode: PaletteMode) => {
  const isDark = mode === "dark";
  return {
    position: "relative",
    p: 0,
    mb: 2,
    borderRadius: "12px",
    border: `1px solid ${isDark ? darkModeTokens.border : scssTokens.darkBlue10}`,
    background: `${isDark ? darkModeTokens.surface : scssTokens.surfaceGlass} !important`,
    boxShadow: `${isDark ? darkModeTokens.shadowSoft : scssTokens.elevationSoft} !important`,
    willChange: "transform, box-shadow",
    transformOrigin: "center bottom",
    transition:
      "box-shadow .3s cubic-bezier(.2,.8,.2,1), transform .3s cubic-bezier(.2,.8,.2,1) !important",
    "&:hover": {
      transform: "translateY(-6px) !important",
      boxShadow: `${isDark ? darkModeTokens.shadowHover : scssTokens.elevationHover} !important`,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "2px",
      background: `linear-gradient(90deg, ${scssTokens.darkBlue}, ${scssTokens.skyBlue}, ${scssTokens.orange})`,
      opacity: isDark ? 0.7 : 0.85,
      pointerEvents: "none",
      zIndex: 1,
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
  };
};

/**
 * getPremiumResourceCardSx — DQ-8674 dark mode
 * Mode-aware version of the per-resource tile inside the Dataset Summary section.
 */
export const getPremiumResourceCardSx = (mode: PaletteMode) => {
  const isDark = mode === "dark";
  return {
    position: "relative",
    borderRadius: "10px",
    border: `1px solid ${isDark ? darkModeTokens.border : scssTokens.inputBoxBorder}`,
    background: isDark ? darkModeTokens.surface : scssTokens.white,
    boxShadow: isDark
      ? "0 1px 4px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.18)"
      : "0 1px 2px rgba(20,42,65,0.04), 0 2px 6px rgba(20,42,65,0.04)",
    willChange: "transform, box-shadow",
    transformOrigin: "center center",
    transition:
      "transform .3s cubic-bezier(.4,0,.2,1), box-shadow .3s cubic-bezier(.4,0,.2,1), background-color .3s cubic-bezier(.4,0,.2,1)",
    "&:hover": {
      transform: "scale(1.02)",
      background: isDark ? brand.navy : scssTokens.white,
      boxShadow: isDark
        ? [
            "inset 0 1px 0 rgba(215,241,255,0.06)",
            "0 4px 12px rgba(0,0,0,0.36)",
            "0 16px 32px rgba(104,158,200,0.18)",
            `0 0 0 1px ${darkModeTokens.border}`,
          ].join(", ")
        : [
            "inset 0 2px 4px rgba(0,0,0,0.05)",
            "0 4px 10px rgba(20,42,65,0.06)",
            "0 16px 32px rgba(83,126,160,0.14)",
            `0 0 0 1px ${scssTokens.darkBlue10}`,
          ].join(", "),
    },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "10px",
      padding: "2px",
      background: `linear-gradient(135deg, ${scssTokens.darkBlue}, ${scssTokens.skyBlue}, ${scssTokens.orange})`,
      WebkitMask:
        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
      WebkitMaskComposite: "xor",
      mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
      maskComposite: "exclude",
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity .3s cubic-bezier(.4,0,.2,1)",
    },
    "&:hover::after": {
      opacity: isDark ? 0.6 : 1,
    },
  };
};

/**
 * getPremiumGridSx — DQ-8674 dark mode
 * Mode-aware version of the premium DataGrid used on the Comparison Dashboard.
 * All rgba row/header values are recomputed from Theme 2.0 brand tokens.
 */
export const getPremiumGridSx = (mode: PaletteMode) => {
  const isDark = mode === "dark";
  return {
    "&.dashboard-premium-grid": {
      border: `1px solid ${isDark ? darkModeTokens.border : scssTokens.darkBlue10}`,
      borderRadius: "10px",
      backgroundColor: isDark
        ? darkModeTokens.surfaceInset
        : scssTokens.surfaceInset,
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: `${isDark ? darkModeTokens.headerBg : "rgba(255,255,255,0.65)"} !important`,
        backdropFilter: "blur(8px) saturate(140%)",
        WebkitBackdropFilter: "blur(8px) saturate(140%)",
        borderBottom: `1px solid ${isDark ? darkModeTokens.border : scssTokens.darkBlue10}`,
        boxShadow: `inset 0 -1px 0 ${isDark ? darkModeTokens.borderSubtle : "rgba(83,126,160,0.04)"}`,
      },
      "& .MuiDataGrid-columnHeader": {
        "&:focus, &:focus-within": { outline: "none" },
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontFamily: "var(--font-heading) !important",
        fontSize: "14px !important",
        fontWeight: "800 !important",
        letterSpacing: "normal !important",
        textTransform: "none",
        color: `${isDark ? brand.ice : scssTokens.darkBlue} !important`,
      },
      "& .MuiDataGrid-cell": {
        fontFamily: "var(--font-primary)",
        fontSize: "14px",
        fontWeight: 400,
        borderBottom: `1px solid ${isDark ? darkModeTokens.border : scssTokens.darkBlue10}`,
        transition:
          "background-color .2s ease-in-out, color .2s ease-in-out, box-shadow .2s ease-in-out",
      },
      "& .MuiDataGrid-row": {
        backgroundColor: "transparent",
        transition:
          "background-color .2s ease-in-out, box-shadow .2s ease-in-out",
        "&:hover": {
          backgroundColor: `${isDark ? darkModeTokens.rowHover : "var(--table-row-hover-bg)"} !important`,
          boxShadow: `inset 0 0 0 1px ${isDark ? darkModeTokens.borderSubtle : "rgba(83,126,160,0.06)"}`,
        },
        "&.Mui-selected": {
          backgroundColor: `${isDark ? darkModeTokens.rowSelected : "rgba(83,126,160,0.08)"} !important`,
          "&:hover": {
            backgroundColor: `${isDark ? darkModeTokens.rowSelectedHover : "rgba(83,126,160,0.10)"} !important`,
          },
        },
      },
      "& .MuiDataGrid-cell.cell-missing": {
        backgroundColor: isDark
          ? "rgba(104,158,200,0.14)"
          : "rgba(104,158,200,0.08)",
        color: isDark ? brand.ice : scssTokens.orange,
        fontStyle: "italic",
      },
      "& .MuiDataGrid-cell.cell-diff": {
        backgroundColor: isDark
          ? "rgba(232,67,54,0.18)"
          : "rgba(232,67,54,0.08)",
        color: isDark ? "#ef6e6e" : scssTokens.badgeDangerBg, // #ef6e6e = --error dark
        fontWeight: 600,
      },
      "& .MuiDataGrid-cell.cell-key": {
        fontWeight: 600,
        color: isDark ? brand.ice : scssTokens.black,
      },
      "& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders": {
        backgroundColor: isDark
          ? darkModeTokens.surfaceInset
          : scssTokens.surfaceInset,
        boxShadow: `1px 0 0 ${isDark ? darkModeTokens.borderSubtle : "rgba(83,126,160,0.06)"}`,
      },
      "& .MuiDataGrid-footerContainer": {
        borderTop: `1px solid ${isDark ? darkModeTokens.border : scssTokens.darkBlue10}`,
        backgroundColor: isDark
          ? darkModeTokens.footerBg
          : "rgba(255,255,255,0.6)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      },
      "&.MuiDataGrid-root": {
        border: "none",
      },
    },
  };
};

// ─── Light-mode backward-compat static exports ────────────────────────────────
// Consumed by RulesListDataGrid (premiumGridSx) and RootCauseAnalysis
// (premiumCardInsetSx). Update those components to use the factory functions
// when they receive dark mode treatment.

// Shared `sx` preset for the 5 premium section cards on the Comparison
// Dashboard. Opaque white surface, multi-layer soft shadow, hover lift, and
// a subtle gradient hairline along the top edge.
export const premiumCardSx = {
  position: "relative",
  p: 0,
  mb: 2,
  borderRadius: "12px",
  border: `1px solid ${scssTokens.darkBlue10}`,
  background: `${scssTokens.surfaceGlass} !important`,
  boxShadow: `${scssTokens.elevationSoft} !important`,
  willChange: "transform, box-shadow",
  transformOrigin: "center bottom",
  transition:
    "box-shadow .3s cubic-bezier(.2,.8,.2,1), transform .3s cubic-bezier(.2,.8,.2,1) !important",
  "&:hover": {
    transform: "translateY(-6px) !important",
    boxShadow: `${scssTokens.elevationHover} !important`,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, ${scssTokens.darkBlue}, ${scssTokens.skyBlue}, ${scssTokens.orange})`,
    opacity: 0.85,
    pointerEvents: "none",
    zIndex: 1,
    borderTopLeftRadius: "12px",
    borderTopRightRadius: "12px",
  },
} as const;

// Premium resource card — used by ComparisonDashboardCard tiles inside the
// Dataset Summary section. Embossed depth, animated gradient border on hover,
// and a subtle scale-up. Stays distinct from the parent section card
// (`premiumCardSx`) by being smaller, snappier, and using a mask-painted
// gradient border that only appears on hover.
export const premiumResourceCardSx = {
  position: "relative",
  borderRadius: "10px",
  border: `1px solid ${scssTokens.inputBoxBorder}`,
  background: scssTokens.white,
  boxShadow: "0 1px 2px rgba(20,42,65,0.04), 0 2px 6px rgba(20,42,65,0.04)",
  willChange: "transform, box-shadow",
  transformOrigin: "center center",
  transition:
    "transform .3s cubic-bezier(.4,0,.2,1), box-shadow .3s cubic-bezier(.4,0,.2,1), background-color .3s cubic-bezier(.4,0,.2,1)",
  "&:hover": {
    transform: "scale(1.02)",
    background: scssTokens.white,
    boxShadow: [
      "inset 0 2px 4px rgba(0,0,0,0.05)",
      "0 4px 10px rgba(20,42,65,0.06)",
      "0 16px 32px rgba(25,107,180,0.14)",
      `0 0 0 1px ${scssTokens.darkBlue10}`,
    ].join(", "),
  },
  // Mask-painted 2px gradient border revealed on hover via ::after.
  // Using two stacked linear-gradients with mask-composite to "punch out"
  // the interior so only the border ring is painted.
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "10px",
    padding: "2px",
    background: `linear-gradient(135deg, ${scssTokens.darkBlue}, ${scssTokens.skyBlue}, ${scssTokens.orange})`,
    WebkitMask:
      "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
    WebkitMaskComposite: "xor",
    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
    maskComposite: "exclude",
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity .3s cubic-bezier(.4,0,.2,1)",
  },
  "&:hover::after": {
    opacity: 1,
  },
} as const;

// Premium DataGrid sx — Phase 2A. Scoped via the `.dashboard-premium-grid`
// class so these styles never leak into other DataGrid instances. Applied by
// RulesListDataTable when `variant="premium"` is passed.
//
// Notes:
// - Header background uses a translucent white tint plus backdrop-filter to
//   produce a frosted-glass effect over the page surface.
// - Headers use uppercase, letter-spaced typography for a "data-heavy"
//   premium aesthetic.
// - Row hover applies a soft tint + inset shadow (no translateY to preserve
//   sticky-header alignment).
// - `cell-missing` class (added by getCellClassName below) gets a warm amber
//   tint for null / empty / UNDEFINED values.
export const premiumGridSx = {
  "&.dashboard-premium-grid": {
    border: `1px solid ${scssTokens.darkBlue10}`,
    borderRadius: "10px",
    backgroundColor: scssTokens.surfaceInset,
    // Column headers — frosted glass with AutoXLR8 typography
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "rgba(255,255,255,0.65) !important",
      backdropFilter: "blur(8px) saturate(140%)",
      WebkitBackdropFilter: "blur(8px) saturate(140%)",
      borderBottom: `1px solid ${scssTokens.darkBlue10}`,
      boxShadow: "inset 0 -1px 0 rgba(25,107,180,0.04)",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:focus, &:focus-within": { outline: "none" },
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontFamily: "var(--font-heading) !important",
      fontSize: "14px !important",
      fontWeight: "800 !important",
      letterSpacing: "normal !important",
      textTransform: "none",
      color: `${scssTokens.darkBlue} !important`,
    },
    // Cells & row separators
    "& .MuiDataGrid-cell": {
      fontFamily: "var(--font-primary)",
      fontSize: "14px",
      fontWeight: 400,
      borderBottom: `1px solid ${scssTokens.darkBlue10}`,
      transition:
        "background-color .2s ease-in-out, color .2s ease-in-out, box-shadow .2s ease-in-out",
    },
    "& .MuiDataGrid-row": {
      backgroundColor: "transparent",
      transition:
        "background-color .2s ease-in-out, box-shadow .2s ease-in-out",
      "&:hover": {
        backgroundColor: "rgba(25,107,180,0.04) !important",
        boxShadow: "inset 0 0 0 1px rgba(25,107,180,0.06)",
      },
      "&.Mui-selected": {
        backgroundColor: "rgba(25,107,180,0.08) !important",
        "&:hover": {
          backgroundColor: "rgba(25,107,180,0.10) !important",
        },
      },
    },
    // Semantic cell classes (set via getCellClassName in the grid wrapper)
    "& .MuiDataGrid-cell.cell-missing": {
      backgroundColor: "rgba(244,132,0,0.08)",
      color: scssTokens.orange,
      fontStyle: "italic",
    },
    "& .MuiDataGrid-cell.cell-diff": {
      backgroundColor: "rgba(232,67,54,0.08)",
      color: scssTokens.badgeDangerBg,
      fontWeight: 600,
    },
    "& .MuiDataGrid-cell.cell-key": {
      fontWeight: 600,
      color: scssTokens.black,
    },
    // Pinned columns get an explicit surface so the frosted header stays
    // consistent over them
    "& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders": {
      backgroundColor: scssTokens.surfaceInset,
      boxShadow: "1px 0 0 rgba(25,107,180,0.06)",
    },
    // Footer
    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${scssTokens.darkBlue10}`,
      backgroundColor: "rgba(255,255,255,0.6)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
    },
    // Outer DataGrid border — drop the default 1px since the wrapper draws it
    "&.MuiDataGrid-root": {
      border: "none",
    },
  },
} as const;

// Inset variant — option B. For nested cards that should sit visually
// "inside" a parent premium card without competing for elevation.
export const premiumCardInsetSx = {
  position: "relative",
  p: 0,
  mb: 1,
  borderRadius: "8px",
  border: `1px solid ${scssTokens.darkBlue10}`,
  background: scssTokens.surfaceInset,
  boxShadow: scssTokens.elevationInset,
  overflow: "hidden",
  transition: "border-color .2s ease, background-color .2s ease",
  "&:hover": {
    borderColor: scssTokens.darkBlue50,
    background: scssTokens.white,
  },
} as const;

// ─── DQ-8527: New MUI theme factory ───────────────────────────────────────────
/**
 * newThemeSettings — DQ-8527 / DQ-8674
 *
 * Returns the ThemeOptions for the new MUI-based theme. All palette values are
 * sourced from the Theme 2.0 scssTokens (which now mirror _variables.scss after
 * the DQ-8674 re-pointing). Typography uses Nexa to align with the rest of the
 * Theme 2.0 system (DQ-8617 brief).
 *
 * AC-1: Exported alongside themeSettings — no existing export is removed.
 * AC-2: Palette keys map to Theme 2.0 token values from _variables.scss.
 * AC-3: Typography uses Nexa font family (aligned with Theme 2.0).
 */
export const newThemeSettings = (): ThemeOptions => ({
  palette: {
    mode: "light",
    primary: {
      main: scssTokens.darkBlue, // --dark-blue: #537ea0 (blueScale[600])
      light: scssTokens.skyBlue, // --sky-blue:  #689ec8 (brand.blue)
      dark: "#3e5e78", // blueScale[700] — hover/active
      contrastText: "#ffffff",
    },
    secondary: {
      main: scssTokens.orange, // --orange: #689ec8 (brand.blue, re-pointed)
      contrastText: "#ffffff",
    },
    error: {
      main: scssTokens.badgeDangerBg, // --badge-danger-bg: #e84336
    },
    success: {
      main: scssTokens.successBg1, // --success-bg-1: rgb(116,184,86)
    },
    background: {
      default: "#ffffff",
      paper: scssTokens.cardBg, // --card-bg: #e6f6ff (blueScale[50])
    },
    text: {
      primary: scssTokens.black, // --black:  #0E1223 (brand.navy)
      secondary: scssTokens.grey, // --grey:   #4f5260 (navyScale[400])
    },
    neutral: {
      main: scssTokens.lightGrey, // --light-grey: #a7a8af (navyScale[200])
    },
  },
  typography: {
    // DQ-8674: aligned with Theme 2.0 — Nexa replaces Inter
    fontFamily: ["nexa", "sans-serif"].join(","),
    fontSize: 12,
    h1: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 24 },
    h2: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 20 },
    h3: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 18 },
    h4: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 16 },
    h5: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 14 },
    h6: { fontFamily: ["nexa", "sans-serif"].join(","), fontSize: 12 },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          backgroundColor: scssTokens.tableHeaderBg, // #e6f6ff (blueScale[50])
        },
        row: {
          "&:nth-of-type(even)": {
            backgroundColor: scssTokens.tableRowAltBg, // 5% of badgeBlueBorder
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: scssTokens.chipBg, // brand.blue 12%
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: scssTokens.darkBlue, // #537ea0 (blueScale[600])
          "&:hover": {
            backgroundColor: "#3e5e78", // blueScale[700]
            boxShadow: `0 0 0 2px ${scssTokens.orange}`, // brand.blue ring
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: scssTokens.inputBoxBorder, // #d3d3d7 (navyScale[100])
        },
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: scssTokens.darkBlue,
          },
          "&.Mui-focused": {
            boxShadow: scssTokens.inputFocusShadow,
          },
        },
      },
    },
  },
});

// Pre-built Theme instance for direct import into App.tsx.
// AC-5: When uiVersion === "new", App.tsx passes this to ThemeProvider.
export const newMuiTheme = createTheme(newThemeSettings());
